import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import {
  Alarm,
  AlertRule,
  AlertEvent,
  OnCallSchedule,
  NotificationChannel,
  NotificationLog,
  CommandRequest,
  CommandResponse,
  GatewayConfigDto,
  GatewayConnection,
  Group,
  GroupWidget,
  LoginRequest,
  LoginResponse,
  Plant,
  PlantKnowledge,
  KnowledgeSummary,
  VoiceSession,
  VoiceSessionTurnsResponse,
  SetVoiceTurnFeedbackRequest,
  SynopticConfig,
  MonthlyStatistics,
  DailyStatistics,
  StatisticsGroup,
  StatisticsMetric,
  SeverityLevel,
  AlarmGroup,
  AlarmResetVariable,
  PlantDetailBundle,
  WeatherCurrent,
  WeatherForecast,
} from '../types';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5288/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send HttpOnly refresh token cookie
});

// Request interceptor: attach JWT token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: auto-refresh token on 401
let isRefreshing = false;
let failedQueue: { resolve: (v: unknown) => void; reject: (e: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

// Helper sleep per backoff
const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Auto-retry su 429 Too Many Requests con backoff esponenziale.
    // Durante navigazione intensa il frontend può superare il rate limit
    // momentaneamente; aspettando 1-2-4 secondi la finestra si libera e la
    // chiamata va a buon fine, senza mostrare errore all'utente.
    if (error.response?.status === 429
        && !originalRequest.url?.includes('/auth/login')
        && !originalRequest.url?.includes('/auth/qr-login')
        && !originalRequest.url?.includes('/auth/force-change-password')) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      if (originalRequest._retryCount <= 3) {
        // Se il server indica Retry-After, rispettalo (sec); altrimenti backoff exp.
        const retryAfterHeader = error.response.headers?.['retry-after'];
        const retryAfterMs = retryAfterHeader
          ? Math.min(parseInt(retryAfterHeader, 10) * 1000, 10_000)
          : Math.min(1000 * Math.pow(2, originalRequest._retryCount - 1), 8_000);
        await sleep(retryAfterMs);
        return apiClient(originalRequest);
      }
      // Esauriti i retry: propaga l'errore
    }

    // Se 401 e non è già un retry né la chiamata di refresh stessa
    if (error.response?.status === 401 && !originalRequest._retry
        && !originalRequest.url?.includes('/auth/refresh')
        && !originalRequest.url?.includes('/auth/login')) {
      if (isRefreshing) {
        // Accoda la richiesta finché il refresh è in corso
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${BASE_URL}/auth/refresh`, {},
          { withCredentials: true }
        );

        const newToken = data.token;
        localStorage.setItem('token', newToken);
        if (data.username) localStorage.setItem('username', data.username);
        if (data.role) localStorage.setItem('role', data.role);

        apiClient.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Refresh fallito → logout
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        localStorage.removeItem('role');
        localStorage.removeItem('isSuperAdmin');
        localStorage.removeItem('companies');
        localStorage.removeItem('activeCompanyId');
        localStorage.removeItem('permissions');
        localStorage.removeItem('accessiblePlantIds');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ═══════════════════════════════════════════════════════════════════════════
// Fase A.3: In-flight deduplication per i GET.
// Se piu' componenti richiedono lo stesso URL+params in contemporanea (es. due
// card montate insieme, StrictMode double-mount in dev, rapido click-rinvio),
// tutte condividono una singola richiesta di rete: i chiamanti successivi si
// agganciano alla Promise gia' in volo invece di aprire nuove connessioni.
//
// Semantica con AbortSignal: ogni consumer mantiene il proprio signal. Se il
// suo signal viene abortito, solo la sua Promise locale viene rigettata — la
// richiesta sottostante prosegue per gli altri consumer, che ricevono comunque
// la risposta (o l'errore finale). Questo evita che un componente che si
// smonta provochi fallimenti a cascata su altri che stanno ancora ascoltando.
//
// Si applica SOLO ai GET. I POST/PUT/DELETE non vengono mai deduplicati
// (una POST inviata due volte intenzionalmente deve restare due POST).
// ═══════════════════════════════════════════════════════════════════════════

type InflightEntry = { promise: Promise<AxiosResponse<unknown>> };
const inflightGets = new Map<string, InflightEntry>();

function buildDedupKey(url: string, params?: Record<string, unknown>): string {
  if (!params || Object.keys(params).length === 0) return url;
  try {
    const sortedKeys = Object.keys(params).sort();
    const serialized = sortedKeys.map(k => `${k}=${JSON.stringify(params[k])}`).join('&');
    return `${url}?${serialized}`;
  } catch {
    return url;
  }
}

function makeCanceledError(): Error {
  const err = new Error('canceled') as Error & { code?: string };
  err.name = 'CanceledError';
  err.code = 'ERR_CANCELED';
  return err;
}

// Preserva riferimento al GET originale prima di sovrascriverlo.
const rawApiGet = apiClient.get.bind(apiClient);

// Sostituisce apiClient.get con la versione deduplicata. Tutte le chiamate
// esistenti (api.get / apiClient.get) beneficiano automaticamente del dedup
// senza modifiche ai call site.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(apiClient as any).get = function dedupedGet<T = unknown>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  const key = buildDedupKey(url, config?.params);
  const signal = config?.signal as AbortSignal | undefined;

  let entry = inflightGets.get(key);
  if (!entry) {
    // La richiesta condivisa parte SENZA il signal del primo caller: altrimenti
    // un suo abort cancellerebbe la richiesta per tutti gli altri consumer.
    // Ogni consumer ottiene il rispetto del proprio signal tramite il wrapper
    // Promise sotto.
    const configNoSignal: AxiosRequestConfig = { ...config };
    delete configNoSignal.signal;
    const promise = rawApiGet(url, configNoSignal) as Promise<AxiosResponse<unknown>>;
    const created: InflightEntry = { promise };
    entry = created;
    inflightGets.set(key, created);
    // Cleanup: rimuovi l'entry quando la richiesta completa (success o failure).
    // Non mettiamo .catch() sulla Promise originale per non alterare il flusso
    // di errore — usiamo .then con due branch che non rilanciano.
    promise.then(
      () => { if (inflightGets.get(key) === created) inflightGets.delete(key); },
      () => { if (inflightGets.get(key) === created) inflightGets.delete(key); }
    );
  }

  const sharedPromise = entry.promise as Promise<AxiosResponse<T>>;
  if (!signal) return sharedPromise;

  // Wrapper per-caller che rispetta il suo AbortSignal senza influenzare la
  // richiesta condivisa.
  return new Promise<AxiosResponse<T>>((resolve, reject) => {
    if (signal.aborted) {
      reject(makeCanceledError());
      return;
    }
    const onAbort = () => {
      cleanup();
      reject(makeCanceledError());
    };
    const cleanup = () => signal.removeEventListener('abort', onAbort);
    signal.addEventListener('abort', onAbort);
    sharedPromise.then(
      (res) => { cleanup(); resolve(res); },
      (err) => { cleanup(); reject(err); }
    );
  });
};

// --- Auth ---

export async function login(request: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/login', request);
  return response.data;
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } catch {
    // Ignora errori di logout — pulisci comunque il client
  }
}

export async function registerUser(data: {
  username: string; fullName: string; email: string; role: string;
}): Promise<{ message: string; emailSent?: boolean; tempPasswordFallback?: string }> {
  const response = await apiClient.post('/auth/register', data);
  return response.data;
}

export async function forceChangePassword(username: string, currentPassword: string, newPassword: string): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/force-change-password', { username, currentPassword, newPassword });
  return response.data;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
  const response = await apiClient.post('/auth/change-password', { currentPassword, newPassword });
  return response.data;
}

export async function adminResetPassword(userId: string): Promise<{ message: string; emailSent?: boolean; tempPasswordFallback?: string }> {
  const response = await apiClient.post('/auth/admin-reset-password', { userId });
  return response.data;
}

export async function updateUser(userId: string, data: {
  fullName?: string; email?: string; phoneNumber?: string; role?: string;
}): Promise<{ message: string }> {
  const response = await apiClient.put(`/auth/users/${userId}`, data);
  return response.data;
}

export async function deleteUser(userId: string): Promise<{ message: string }> {
  const response = await apiClient.delete(`/auth/users/${userId}`);
  return response.data;
}

export async function getAppUsers(): Promise<{
  id: string; userName: string; fullName: string; email: string; phoneNumber?: string;
  role: string; lastLoginAt?: string; isSuperAdmin: boolean;
  pushDevices: { id: number; deviceName?: string; createdAt: string; lastUsedAt?: string }[];
}[]> {
  const response = await apiClient.get('/auth/users');
  return response.data;
}

export async function adminDeletePushSubscription(subscriptionId: number): Promise<{ message: string }> {
  const response = await apiClient.delete(`/auth/push-subscription/${subscriptionId}`);
  return response.data;
}

// --- Auth: Companies & Permissions ---

export async function getMyCompanies(): Promise<{ id: number; name: string; code: string }[]> {
  const response = await apiClient.get('/auth/companies');
  return response.data;
}

export async function getMyPermissions(companyId?: number): Promise<{
  companyId: number;
  companyName: string;
  policyGroupName: string;
  permissions: Record<string, string>;
  accessiblePlantIds: number[];
}> {
  const response = await apiClient.get('/auth/permissions', { params: { companyId } });
  return response.data;
}

export async function getCurrentUser(): Promise<{
  userName: string; fullName: string; email: string; role: string; isSuperAdmin: boolean;
}> {
  const response = await apiClient.get('/auth/me');
  return response.data;
}

// --- Admin: Companies ---

export async function getCompanies() {
  const response = await apiClient.get('/admin/companies');
  return response.data;
}

export async function createCompany(data: { name: string; code: string; description?: string }) {
  const response = await apiClient.post('/admin/companies', data);
  return response.data;
}

export async function updateCompany(id: number, data: { name?: string; description?: string; isActive?: boolean }) {
  const response = await apiClient.put(`/admin/companies/${id}`, data);
  return response.data;
}

export async function deleteCompany(id: number) {
  const response = await apiClient.delete(`/admin/companies/${id}`);
  return response.data;
}

export async function getCompanyPlants(companyId: number) {
  const response = await apiClient.get(`/admin/companies/${companyId}/plants`);
  return response.data;
}

export async function assignPlantToCompany(companyId: number, plantId: number) {
  const response = await apiClient.post(`/admin/companies/${companyId}/plants`, { plantId });
  return response.data;
}

export async function removePlantFromCompany(companyId: number, plantId: number) {
  const response = await apiClient.delete(`/admin/companies/${companyId}/plants/${plantId}`);
  return response.data;
}

export async function getCompanyUsers(companyId: number) {
  const response = await apiClient.get(`/admin/companies/${companyId}/users`);
  return response.data;
}

export async function assignUserToCompany(companyId: number, userId: string, policyGroupId?: number) {
  const response = await apiClient.post(`/admin/companies/${companyId}/users`, { userId, policyGroupId });
  return response.data;
}

export async function removeUserFromCompany(companyId: number, userId: string) {
  const response = await apiClient.delete(`/admin/companies/${companyId}/users/${userId}`);
  return response.data;
}

// --- Admin: Policy Groups ---

export async function getPolicyGroups(companyId?: number) {
  const response = await apiClient.get('/admin/policy-groups', { params: { companyId } });
  return response.data;
}

export async function createPolicyGroup(data: {
  name: string; description?: string; companyId?: number;
  permissions?: { section: string; accessLevel: string }[];
}) {
  const response = await apiClient.post('/admin/policy-groups', data);
  return response.data;
}

export async function updatePolicyGroup(id: number, data: { name?: string; description?: string }) {
  const response = await apiClient.put(`/admin/policy-groups/${id}`, data);
  return response.data;
}

export async function deletePolicyGroup(id: number) {
  const response = await apiClient.delete(`/admin/policy-groups/${id}`);
  return response.data;
}

export async function getPolicyGroupPermissions(id: number): Promise<{ section: string; accessLevel: string }[]> {
  const response = await apiClient.get(`/admin/policy-groups/${id}/permissions`);
  return response.data;
}

export async function updatePolicyGroupPermissions(id: number, permissions: { section: string; accessLevel: string }[]) {
  const response = await apiClient.put(`/admin/policy-groups/${id}/permissions`, permissions);
  return response.data;
}

export async function setUserPolicyGroup(userId: string, companyId: number, policyGroupId: number) {
  const response = await apiClient.put(`/admin/users/${userId}/companies/${companyId}/policy-group`, { policyGroupId });
  return response.data;
}

export async function getPermissionSections(): Promise<{ key: string; label: string }[]> {
  const response = await apiClient.get('/admin/permission-sections');
  return response.data;
}

export async function getAuditLogs(params?: {
  userId?: string; action?: string; from?: string; to?: string; page?: number; pageSize?: number;
}) {
  const response = await apiClient.get('/admin/audit-logs', { params });
  return response.data;
}

// --- Plants ---

export async function getPlants(): Promise<Plant[]> {
  const response = await apiClient.get<Plant[]>('/plants');
  return response.data;
}

export async function getPlant(plantId: string, signal?: AbortSignal): Promise<Plant> {
  const response = await apiClient.get<Plant>(`/plants/${plantId}`, { signal });
  return response.data;
}

// Fase A.1: batch endpoint — ritorna plant + groups + metriche + widget +
// variabili in un'unica chiamata, rimpiazzando il pattern 5N+2 chiamate.
// Usa stesse semantiche di auth/abort di getPlant/getGroups.
export async function getPlantDetailBundle(plantId: string, signal?: AbortSignal): Promise<PlantDetailBundle> {
  const response = await apiClient.get<PlantDetailBundle>(`/plants/${plantId}/detail-bundle`, { signal });
  return response.data;
}

// GIS — meteo corrente per l'impianto.
// Provider chain backend: Azure Maps Gen2 (AccuWeather) → Open-Meteo fallback.
// Cache Redis 15 min. Ritorna null se l'impianto non ha coordinate configurate
// o il servizio meteo e' down: il caller mostra placeholder invece di toast.
export async function getPlantCurrentWeather(
  plantId: number | string,
  signal?: AbortSignal
): Promise<WeatherCurrent | null> {
  try {
    const response = await apiClient.get<WeatherCurrent>(`/plants/${plantId}/weather/current`, { signal });
    return response.data;
  } catch (err: any) {
    // 404 (coords mancanti) e 502 (provider down) sono condizioni attese: null silenzioso.
    if (err?.response?.status === 404 || err?.response?.status === 502) return null;
    throw err;
  }
}

// GIS — forecast meteo orario+giornaliero. Default 24h hourly + 7g daily.
// Cache Redis 60 min lato backend. Stesso provider chain di current.
export async function getPlantWeatherForecast(
  plantId: number | string,
  hours: number = 24,
  days: number = 7,
  signal?: AbortSignal
): Promise<WeatherForecast | null> {
  try {
    const response = await apiClient.get<WeatherForecast>(
      `/plants/${plantId}/weather/forecast`,
      { params: { hours, days }, signal }
    );
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 404 || err?.response?.status === 502) return null;
    throw err;
  }
}

// --- Severity Levels per impianto ---
export async function getSeverityLevels(plantId: number): Promise<SeverityLevel[]> {
  const response = await apiClient.get<SeverityLevel[]>(`/plants/${plantId}/severity-levels`);
  return response.data;
}

export async function createSeverityLevel(plantId: number, data: { key: string; label: string; color: string; priority: number }): Promise<SeverityLevel> {
  const response = await apiClient.post<SeverityLevel>(`/plants/${plantId}/severity-levels`, data);
  return response.data;
}

export async function updateSeverityLevel(plantId: number, levelId: number, data: { key?: string; label?: string; color?: string; priority: number }): Promise<SeverityLevel> {
  const response = await apiClient.put<SeverityLevel>(`/plants/${plantId}/severity-levels/${levelId}`, data);
  return response.data;
}

export async function deleteSeverityLevel(plantId: number, levelId: number): Promise<{ message: string }> {
  const response = await apiClient.delete(`/plants/${plantId}/severity-levels/${levelId}`);
  return response.data;
}

// --- Alarm Groups per impianto ---
export async function getAlarmGroups(plantId: number): Promise<AlarmGroup[]> {
  const response = await apiClient.get<AlarmGroup[]>(`/plants/${plantId}/alarm-groups`);
  return response.data;
}

export async function createAlarmGroup(plantId: number, data: { name: string; description?: string }): Promise<AlarmGroup> {
  const response = await apiClient.post<AlarmGroup>(`/plants/${plantId}/alarm-groups`, data);
  return response.data;
}

export async function updateAlarmGroup(plantId: number, groupId: number, data: { name?: string; description?: string }): Promise<AlarmGroup> {
  const response = await apiClient.put<AlarmGroup>(`/plants/${plantId}/alarm-groups/${groupId}`, data);
  return response.data;
}

export async function deleteAlarmGroup(plantId: number, groupId: number): Promise<{ message: string }> {
  const response = await apiClient.delete(`/plants/${plantId}/alarm-groups/${groupId}`);
  return response.data;
}

// --- Command Policy Groups per impianto ---
//
// Ogni variabile con commandEnabled=true DEVE essere associata a un
// CommandPolicyGroup. L'utente può eseguire un comando sse è membro del gruppo
// della variabile. Vedi backend WriteVariableCommand per il check completo.

export interface CommandPolicyGroupDto {
  id: number;
  plantId: number;
  name: string;
  description: string;
  color?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  variableCount: number;
  memberUserIds: string[];
}

export interface AccessibleCommandPolicyGroupDto {
  id: number;
  plantId: number;
  name: string;
  color?: string;
}

export async function getCommandPolicyGroups(plantId: number): Promise<CommandPolicyGroupDto[]> {
  const response = await apiClient.get<CommandPolicyGroupDto[]>(`/plants/${plantId}/command-policy-groups`);
  return response.data;
}

export async function createCommandPolicyGroup(plantId: number, data: { name: string; description?: string; color?: string }): Promise<CommandPolicyGroupDto> {
  const response = await apiClient.post<CommandPolicyGroupDto>(`/plants/${plantId}/command-policy-groups`, data);
  return response.data;
}

export async function updateCommandPolicyGroup(plantId: number, groupId: number, data: { name?: string; description?: string; color?: string }): Promise<CommandPolicyGroupDto> {
  const response = await apiClient.put<CommandPolicyGroupDto>(`/plants/${plantId}/command-policy-groups/${groupId}`, data);
  return response.data;
}

export async function deleteCommandPolicyGroup(plantId: number, groupId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/command-policy-groups/${groupId}`);
}

/** Sostituisce l'elenco dei membri (set semantics). */
export async function setCommandPolicyGroupMembers(plantId: number, groupId: number, userIds: string[]): Promise<void> {
  await apiClient.put(`/plants/${plantId}/command-policy-groups/${groupId}/members`, userIds);
}

/** Lista CommandPolicyGroup a cui l'utente corrente appartiene (per UX disabled bottoni). */
export async function getMyCommandPolicyGroups(): Promise<AccessibleCommandPolicyGroupDto[]> {
  const response = await apiClient.get<AccessibleCommandPolicyGroupDto[]>(`/auth/command-policy-groups`);
  return response.data;
}

// --- Gateway Connect / Remote Access (fase 1: catalogo + finestre + audit) ---
// Fase 2 aggiungerà: open/renew/close session, MFA push challenge, polling stato tunnel.

export type RemoteDeviceType = 'Plc' | 'Hmi' | 'Ipc' | 'Server' | 'NetworkDevice' | 'Other';

export interface RemoteDeviceDto {
  id: number;
  plantId: number;
  name: string;
  deviceType: RemoteDeviceType;
  ipAddress: string;
  allowedPorts: number[];
  description: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface RemoteAccessWindowDto {
  id: number;
  plantId: number;
  openedAt: string;
  closesAt: string;
  isManuallyClosed: boolean;
  closedAt?: string;
  closedBy?: string;
  openedByUserId: string;
  openedByUserName: string;
  reason: string;
  isActiveNow: boolean;
}

export type RemoteAccessSessionEndReason =
  | 'UserClosed' | 'Expired' | 'KillSwitch' | 'WindowClosed' | 'SystemError' | 'HeartbeatLost';

export interface RemoteAccessSessionDto {
  id: number;
  remoteDeviceId: number;
  remoteDeviceName: string;
  engineerUserId: string;
  engineerUserName: string;
  startedAt: string;
  expiresAt: string;
  endedAt?: string;
  endReason?: RemoteAccessSessionEndReason;
  clientPublicIp?: string;
  engineerVirtualIp: string;
  bytesSent: number;
  bytesReceived: number;
  isActive: boolean;
}

// Devices
export async function getRemoteDevices(plantId: number): Promise<RemoteDeviceDto[]> {
  const response = await apiClient.get<RemoteDeviceDto[]>(`/plants/${plantId}/remote-access/devices`);
  return response.data;
}
export async function createRemoteDevice(plantId: number, data: {
  name: string; deviceType: RemoteDeviceType; ipAddress: string; allowedPorts: number[]; description?: string;
}): Promise<RemoteDeviceDto> {
  const response = await apiClient.post<RemoteDeviceDto>(`/plants/${plantId}/remote-access/devices`, data);
  return response.data;
}
export async function updateRemoteDevice(plantId: number, deviceId: number, data: {
  name?: string; deviceType?: RemoteDeviceType; ipAddress?: string; allowedPorts?: number[]; description?: string; isActive?: boolean;
}): Promise<RemoteDeviceDto> {
  const response = await apiClient.put<RemoteDeviceDto>(`/plants/${plantId}/remote-access/devices/${deviceId}`, data);
  return response.data;
}
export async function deleteRemoteDevice(plantId: number, deviceId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/remote-access/devices/${deviceId}`);
}

// Windows
export async function getRemoteAccessWindows(plantId: number, limit = 50): Promise<RemoteAccessWindowDto[]> {
  const response = await apiClient.get<RemoteAccessWindowDto[]>(`/plants/${plantId}/remote-access/windows`, { params: { limit } });
  return response.data;
}
export async function getCurrentRemoteAccessWindow(plantId: number): Promise<RemoteAccessWindowDto | null> {
  try {
    const response = await apiClient.get<RemoteAccessWindowDto>(`/plants/${plantId}/remote-access/windows/current`);
    return response.data;
  } catch (e: any) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}
export async function openRemoteAccessWindow(plantId: number, data: { durationMinutes: number; reason: string }): Promise<RemoteAccessWindowDto> {
  const response = await apiClient.post<RemoteAccessWindowDto>(`/plants/${plantId}/remote-access/windows`, data);
  return response.data;
}
export async function closeRemoteAccessWindow(plantId: number, windowId: number): Promise<void> {
  await apiClient.post(`/plants/${plantId}/remote-access/windows/${windowId}/close`);
}

// Sessions (audit)
export async function getRemoteAccessSessions(plantId: number, activeOnly = false, limit = 100): Promise<RemoteAccessSessionDto[]> {
  const response = await apiClient.get<RemoteAccessSessionDto[]>(`/plants/${plantId}/remote-access/sessions`, {
    params: { activeOnly, limit },
  });
  return response.data;
}

// --- Variable Command (scrittura) ---
export async function getAlarmResetMap(plantId: number): Promise<AlarmResetVariable[]> {
  const response = await apiClient.get<AlarmResetVariable[]>(`/plants/${plantId}/alarm-reset-map`);
  return response.data;
}

export async function sendVariableCommand(plantId: number, variableId: number, value: string): Promise<{ requestId: string }> {
  const response = await apiClient.post(`/plants/${plantId}/variables/${variableId}/command`, { value });
  return response.data;
}

export async function createPlant(data: { name: string; location?: string; description?: string; totalPowerMW?: number }): Promise<Plant> {
  const response = await apiClient.post<Plant>('/plants', data);
  return response.data;
}

export async function updatePlant(plantId: number, data: {
  name?: string; location?: string; description?: string;
  totalPowerMW?: number; latitude?: number; longitude?: number;
}): Promise<Plant> {
  const response = await apiClient.put<Plant>(`/plants/${plantId}`, data);
  return response.data;
}

export async function deletePlant(plantId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}`);
}

// --- Groups ---

export async function getGroups(plantId: string, signal?: AbortSignal): Promise<Group[]> {
  const response = await apiClient.get<Group[]>(`/plants/${plantId}/groups`, { signal });
  return response.data;
}

// --- Group Widgets ---

export async function getGroupWidgets(plantId: string, groupId: number): Promise<GroupWidget[]> {
  const response = await apiClient.get<GroupWidget[]>(`/plants/${plantId}/groups/${groupId}/widgets`);
  return response.data;
}

export async function batchSaveGroupWidgets(plantId: string, groupId: number, widgets: Array<{
  id?: number; type: string; variableId: number | null; commandVariableId: number | null;
  sortOrder: number; mappings: Array<{ value: number; label: string; color: string }>;
}>): Promise<GroupWidget[]> {
  const response = await apiClient.post<GroupWidget[]>(`/plants/${plantId}/groups/${groupId}/widgets/batch`, { widgets });
  return response.data;
}

// --- Alarms ---

export async function getAlarms(plantId?: string): Promise<Alarm[]> {
  const url = plantId ? `/alarms?plantId=${plantId}` : '/alarms';
  const response = await apiClient.get<Alarm[]>(url);
  return response.data;
}

export async function acknowledgeAlarm(alarmId: string): Promise<void> {
  await apiClient.post(`/alarms/${alarmId}/acknowledge`);
}

export async function resetAlarm(alarmId: string): Promise<void> {
  await apiClient.post(`/alarms/${alarmId}/reset`);
}

export async function getAlarmHistory(params?: { plantId?: string; from?: string; to?: string }): Promise<Alarm[]> {
  const response = await apiClient.get<Alarm[]>('/alarms/history', { params });
  return response.data;
}

// --- Commands ---

export async function sendCommand(request: CommandRequest): Promise<CommandResponse> {
  const response = await apiClient.post<CommandResponse>('/commands', request);
  return response.data;
}

// --- Synoptic ---

export async function getSynopticConfig(plantId: string): Promise<SynopticConfig> {
  const response = await apiClient.get<SynopticConfig>(`/synoptic/${plantId}`);
  return response.data;
}

export async function saveSynopticConfig(config: SynopticConfig): Promise<SynopticConfig> {
  const response = await apiClient.put<SynopticConfig>(`/synoptic/${config.plantId}`, config);
  return response.data;
}

// --- Gateway ---

export async function getConnectedGateways(): Promise<GatewayConnection[]> {
  const response = await apiClient.get<GatewayConnection[]>('/gateway/connected');
  return response.data;
}

export async function getGatewayStatus(gatewayId: string): Promise<boolean> {
  const response = await apiClient.get<boolean>(`/gateway/${gatewayId}/status`);
  return response.data;
}

export async function writeGatewayVariable(gatewayId: string, variableName: string, value: string): Promise<void> {
  await apiClient.post(`/gateway/${gatewayId}/write`, { variableName, value });
}

export async function getGatewayConfig(gatewayId: string): Promise<GatewayConfigDto> {
  const response = await apiClient.get<GatewayConfigDto>(`/gateway/${gatewayId}/config`);
  return response.data;
}

export async function saveGatewayConfig(gatewayId: string, config: GatewayConfigDto): Promise<GatewayConfigDto> {
  const response = await apiClient.put<GatewayConfigDto>(`/gateway/${gatewayId}/config`, config);
  return response.data;
}

export async function resolveGatewayConfig(gatewayId: string, action: 'useServer' | 'useGateway'): Promise<void> {
  await apiClient.post(`/gateway/${gatewayId}/resolve-config`, { action });
}

export async function restartGatewayDriver(gatewayId: string, driverName: string): Promise<void> {
  await apiClient.post(`/gateway/${gatewayId}/restart-driver`, { driverName });
}

export async function getPlantGateways(plantId: string): Promise<GatewayConnection[]> {
  const response = await apiClient.get<GatewayConnection[]>(`/gateway/plant/${plantId}/connected`);
  return response.data;
}

export async function syncPlantToGateway(plantId: string): Promise<{ gatewayId: string; configVersion: number; variableCount: number }> {
  const response = await apiClient.post(`/gateway/plant/${plantId}/sync-to-gateway`);
  return response.data;
}

// --- Gateway API Key Management ---

export interface ApiKeyInfo {
  id: number;
  gatewayId: string;
  plantId: number;
  keyPrefix: string;
  description: string;
  createdAt: string;
  expiresAt: string | null;
  isActive: boolean;
  lastUsedAt: string | null;
}

export interface GenerateKeyResponse {
  keyId: number;
  apiKey: string;
  keyPrefix: string;
  gatewayId: string;
  plantId: number;
  createdAt: string;
}

export async function generateGatewayApiKey(plantId: string, gatewayId: string, description?: string): Promise<GenerateKeyResponse> {
  const response = await apiClient.post(`/gateway/plant/${plantId}/generate-key`, { gatewayId, description });
  return response.data;
}

export async function getGatewayApiKeys(plantId: string): Promise<ApiKeyInfo[]> {
  const response = await apiClient.get(`/gateway/plant/${plantId}/keys`);
  return response.data;
}

export async function revokeGatewayApiKey(keyId: number): Promise<void> {
  await apiClient.delete(`/gateway/keys/${keyId}`);
}

// --- Gateway v2.0: Diagnostica, CLI Remoto, Lifecycle ---

export async function getGatewayDiagnostics(gatewayId: string): Promise<import('../types').DiagnosticReport> {
  const response = await apiClient.get(`/gateway/${gatewayId}/diagnostics`);
  return response.data;
}

export async function requestGatewayDiagnostics(gatewayId: string): Promise<void> {
  await apiClient.post(`/gateway/${gatewayId}/request-diagnostics`);
}

export async function getGatewayDiagnosticEvents(gatewayId: string, limit = 50): Promise<import('../types').DiagnosticEvent[]> {
  const response = await apiClient.get(`/gateway/${gatewayId}/diagnostics/events`, { params: { limit } });
  return response.data;
}

export async function executeGatewayCommand(gatewayId: string, command: string): Promise<import('../types').CommandResult> {
  const response = await apiClient.post(`/gateway/${gatewayId}/execute`, { command });
  return response.data;
}

export async function startGatewayLogStream(gatewayId: string): Promise<{ requestId: string }> {
  const response = await apiClient.post(`/gateway/${gatewayId}/logs/start`);
  return response.data;
}

export async function stopGatewayLogStream(gatewayId: string, requestId?: string): Promise<void> {
  await apiClient.post(`/gateway/${gatewayId}/logs/stop`, { requestId });
}

export async function getGatewayLogs(gatewayId: string, since: number = 0): Promise<import('../types').LogEntry[]> {
  const response = await apiClient.get(`/gateway/${gatewayId}/logs`, { params: { since } });
  return response.data;
}

export async function shutdownGateway(gatewayId: string): Promise<void> {
  await apiClient.post(`/gateway/${gatewayId}/shutdown`);
}

export async function restartGateway(gatewayId: string): Promise<void> {
  await apiClient.post(`/gateway/${gatewayId}/restart-gateway`);
}

export async function getGatewayLifecycleEvents(gatewayId: string, limit = 50): Promise<import('../types').LifecycleEvent[]> {
  const response = await apiClient.get(`/gateway/${gatewayId}/lifecycle-events`, { params: { limit } });
  return response.data;
}

export async function getGatewayStartupDiagnostics(gatewayId: string): Promise<import('../types').StartupDiagnostics> {
  const response = await apiClient.get(`/gateway/${gatewayId}/startup-diagnostics`);
  return response.data;
}

export async function uploadGatewayFirmware(gatewayId: string, file: File): Promise<{
  fileName: string; size: number; hash: string; downloadUrl: string; storedPath: string;
}> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`/gateway/${gatewayId}/firmware/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function startGatewayFirmwareUpdate(gatewayId: string, downloadUrl: string, expectedHash: string): Promise<{ updateId: string }> {
  const response = await apiClient.post(`/gateway/${gatewayId}/firmware/update`, { downloadUrl, expectedHash });
  return response.data;
}

export async function getGatewayFirmwareStatus(gatewayId: string): Promise<import('../types').FirmwareUpdateStatus> {
  const response = await apiClient.get(`/gateway/${gatewayId}/firmware/status`);
  return response.data;
}

export async function getGatewayMetrics(gatewayId: string, name?: string, from?: string, to?: string): Promise<import('../types').CommandResult> {
  const response = await apiClient.get(`/gateway/${gatewayId}/metrics`, { params: { name, from, to } });
  return response.data;
}

// --- OPC UA Gateway Direct APIs ---
// Queste API chiamano direttamente il web server del gateway (porta 5100)
// per operazioni che richiedono accesso diretto alla sessione OPC UA.

const GATEWAY_DIRECT_URL = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:5100';

const gatewayClient: AxiosInstance = axios.create({
  baseURL: GATEWAY_DIRECT_URL,
  headers: { 'Content-Type': 'application/json' },
});

export async function opcUaBrowse(driverName: string, nodeId?: string, maxResults?: number): Promise<{
  driverName: string;
  parentNodeId: string;
  count: number;
  nodes: import('../types').OpcUaBrowseNode[];
}> {
  const params: Record<string, string> = {};
  if (nodeId) params.nodeId = nodeId;
  if (maxResults) params.maxResults = String(maxResults);
  const response = await gatewayClient.get(`/api/drivers/${driverName}/browse`, { params });
  return response.data;
}

export async function getOpcUaSubscriptions(driverName: string): Promise<{
  driverName: string;
  subscriptions: import('../types').OpcUaSubscriptionConfig[];
}> {
  const response = await gatewayClient.get(`/api/drivers/${driverName}/subscriptions`);
  return response.data;
}

export async function saveOpcUaSubscriptions(
  driverName: string,
  subscriptions: import('../types').OpcUaSubscriptionConfig[]
): Promise<{ success: boolean; count: number }> {
  const response = await gatewayClient.post(`/api/drivers/${driverName}/subscriptions`, subscriptions);
  return response.data;
}

export async function resubscribeOpcUaDriver(driverName: string): Promise<{ success: boolean; subscribedVariables: number }> {
  const response = await gatewayClient.post(`/api/drivers/${driverName}/resubscribe`);
  return response.data;
}

export async function getGatewayDrivers(): Promise<Array<{
  name: string;
  type: string;
  connected: boolean;
  verboseLogging: boolean;
}>> {
  const response = await gatewayClient.get('/api/drivers');
  return response.data;
}

// ───────── Documents ─────────

export interface PlantDocumentDto {
  id: number;
  plantId: number;
  /** Cartella corrente (null = legacy/orfano). Il backend popola FolderId per tutti i nuovi upload. */
  folderId: number | null;
  /** LEGACY (denormalizzato dal nome cartella root) — mantenuto per voice tools / vecchio frontend. */
  category: string;
  title: string;
  description?: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  uploadedAt: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface DocumentCategorySummary {
  category: string;
  documentCount: number;
}

/** Singola cartella, flat. */
export interface DocumentFolderDto {
  id: number;
  plantId: number;
  parentFolderId: number | null;
  name: string;
  color?: string;
  icon?: string;
  sortOrder: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  documentCount: number;
  childFolderCount: number;
}

/** Nodo dell'albero cartelle con figli ricorsivi. */
export interface DocumentFolderTreeNode {
  id: number;
  plantId: number;
  parentFolderId: number | null;
  name: string;
  color?: string;
  icon?: string;
  sortOrder: number;
  /** Documenti direttamente in questa cartella (non nei discendenti). */
  documentCount: number;
  /** Documenti diretti + somma ricorsiva nei discendenti. */
  totalDocumentCount: number;
  children: DocumentFolderTreeNode[];
}

export interface CreateDocumentFolderInput {
  parentFolderId?: number | null;
  name: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
}

export interface UpdateDocumentFolderInput {
  name?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
  parentFolderId?: number | null;
  /** Spostare a root (dato che JSON non distingue "campo assente" da "esplicito null" in axios). */
  moveToRoot?: boolean;
}

export interface UpdatePlantDocumentInput {
  title: string;
  description?: string | null;
  folderId?: number | null;
}

/** LEGACY — esiste per il vecchio frontend pre-migration. Nuovi usi: `getDocumentFolderTree`. */
export async function getDocumentCategories(plantId: number): Promise<string[]> {
  const response = await apiClient.get<string[]>(`/plants/${plantId}/documents/categories`);
  return response.data;
}

/**
 * Lista documenti. Filtri:
 * - `folderId`: solo documenti in quella cartella (escluse sotto-cartelle)
 * - `folderIdRecursive`: include i discendenti
 * - `search`: full-text "contains" su Title + Description + FileName
 * - `category`: LEGACY (filtra per stringa categoria denormalizzata)
 */
export async function getDocuments(
  plantId: number,
  opts: { category?: string; folderId?: number; folderIdRecursive?: boolean; search?: string } = {},
): Promise<PlantDocumentDto[]> {
  const response = await apiClient.get<PlantDocumentDto[]>(`/plants/${plantId}/documents`, {
    params: opts,
  });
  return response.data;
}

export async function getDocumentSummary(plantId: number): Promise<DocumentCategorySummary[]> {
  const response = await apiClient.get<DocumentCategorySummary[]>(`/plants/${plantId}/documents/summary`);
  return response.data;
}

export async function uploadDocument(
  plantId: number,
  file: File,
  opts: { folderId?: number; category?: string; title: string; description?: string },
): Promise<PlantDocumentDto> {
  const formData = new FormData();
  formData.append('file', file);
  if (opts.folderId != null) formData.append('folderId', String(opts.folderId));
  if (opts.category) formData.append('category', opts.category);
  formData.append('title', opts.title);
  if (opts.description) formData.append('description', opts.description);
  const response = await apiClient.post<PlantDocumentDto>(`/plants/${plantId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function updateDocumentMetadata(
  plantId: number,
  docId: number,
  input: UpdatePlantDocumentInput,
): Promise<PlantDocumentDto> {
  const response = await apiClient.put<PlantDocumentDto>(
    `/plants/${plantId}/documents/${docId}`,
    input,
  );
  return response.data;
}

// ─── Folders CRUD ───────────────────────────────────────────────

export async function getDocumentFolderTree(plantId: number): Promise<DocumentFolderTreeNode[]> {
  const response = await apiClient.get<DocumentFolderTreeNode[]>(
    `/plants/${plantId}/documents/folders/tree`,
  );
  return response.data;
}

export async function getDocumentFolders(plantId: number): Promise<DocumentFolderDto[]> {
  const response = await apiClient.get<DocumentFolderDto[]>(`/plants/${plantId}/documents/folders`);
  return response.data;
}

export async function createDocumentFolder(
  plantId: number,
  input: CreateDocumentFolderInput,
): Promise<DocumentFolderDto> {
  const response = await apiClient.post<DocumentFolderDto>(
    `/plants/${plantId}/documents/folders`,
    input,
  );
  return response.data;
}

export async function updateDocumentFolder(
  plantId: number,
  folderId: number,
  input: UpdateDocumentFolderInput,
): Promise<DocumentFolderDto> {
  const response = await apiClient.put<DocumentFolderDto>(
    `/plants/${plantId}/documents/folders/${folderId}`,
    input,
  );
  return response.data;
}

export async function deleteDocumentFolder(plantId: number, folderId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/documents/folders/${folderId}`);
}

export function getDocumentDownloadUrl(plantId: number, docId: number): string {
  return `${BASE_URL}/plants/${plantId}/documents/${docId}/download`;
}

export function getDocumentPreviewUrl(plantId: number, docId: number): string {
  return `${BASE_URL}/plants/${plantId}/documents/${docId}/preview`;
}

export interface BlobUrlResponse {
  url: string;
  usesBlobStorage: boolean;
  expiresAt: string;
}

/**
 * Chiede al backend un SAS URL di anteprima (valido ~30 min, apribile direttamente in iframe/img).
 * Se il backend non usa blob storage (fallback filesystem in dev), usesBlobStorage=false e url="".
 */
export async function getDocumentPreviewSasUrl(
  plantId: number, docId: number
): Promise<BlobUrlResponse> {
  const response = await apiClient.get<BlobUrlResponse>(
    `/plants/${plantId}/documents/${docId}/preview-url`
  );
  return response.data;
}

/** Analogo a {@link getDocumentPreviewSasUrl} ma per il download (Content-Disposition attachment). */
export async function getDocumentDownloadSasUrl(
  plantId: number, docId: number
): Promise<BlobUrlResponse> {
  const response = await apiClient.get<BlobUrlResponse>(
    `/plants/${plantId}/documents/${docId}/download-url`
  );
  return response.data;
}

/**
 * Risolve l'URL da mettere in un iframe/img per l'anteprima:
 * - prova prima il SAS URL (diretto su Azure Blob Storage, niente token Bearer necessario)
 * - se non disponibile, ripiega su blob URL ottenuto con fetch+blob
 */
export async function resolveDocumentPreviewUrl(
  plantId: number, docId: number
): Promise<string> {
  try {
    const sas = await getDocumentPreviewSasUrl(plantId, docId);
    if (sas.usesBlobStorage && sas.url) return sas.url;
  } catch {
    // fallback sotto
  }
  // Fallback: scarica via backend e crea un object URL locale
  const response = await apiClient.get(
    `/plants/${plantId}/documents/${docId}/preview`,
    { responseType: 'blob' }
  );
  return URL.createObjectURL(response.data);
}

export async function deleteDocument(plantId: number, docId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/documents/${docId}`);
}

/**
 * Ruota un documento PDF in modo persistente (sovrascrive il blob).
 * `degrees` deve essere un multiplo di 90 (es. 90, -90, 180).
 * La rotazione si applica a tutte le pagine del PDF.
 */
export async function rotateDocument(
  plantId: number,
  docId: number,
  degrees: number
): Promise<void> {
  await apiClient.post(
    `/plants/${plantId}/documents/${docId}/rotate`,
    null,
    { params: { degrees } }
  );
}

// --- Variable History ---

export async function getVariableHistory(
  plantId: string, variables: string[], from: string, to: string, maxPoints = 1000
): Promise<import('../types').VariableHistorySeries[]> {
  const response = await apiClient.get(`/plants/${plantId}/history`, {
    params: { variables: variables.join(','), from, to, maxPoints },
  });
  return response.data;
}

export async function getVariableAggregates(
  plantId: string, variable: string, from: string, to: string, period?: number
): Promise<import('../types').VariableAggregateSeries> {
  const response = await apiClient.get(`/plants/${plantId}/history/aggregates`, {
    params: { variable, from, to, period },
  });
  return response.data;
}

export async function getHistoryVariables(plantId: string): Promise<import('../types').HistoryVariable[]> {
  const response = await apiClient.get(`/plants/${plantId}/history/variables`);
  return response.data;
}

export async function exportHistoryCSV(
  plantId: string, variables: string[], from: string, to: string
): Promise<Blob> {
  const response = await apiClient.get(`/plants/${plantId}/history/export`, {
    params: { variables: variables.join(','), from, to, format: 'csv' },
    responseType: 'blob',
  });
  return response.data;
}

// --- Statistics ---

export async function getStatistics(
  plantId: string,
  yearFrom?: number,
  yearTo?: number,
  groupId?: number,
  metricTag?: string,
): Promise<MonthlyStatistics[]> {
  const response = await apiClient.get<MonthlyStatistics[]>(`/plants/${plantId}/statistics`, {
    params: { yearFrom, yearTo, groupId, metricTag },
  });
  return response.data;
}

export async function getStatisticsYears(plantId: string): Promise<number[]> {
  const response = await apiClient.get<number[]>(`/plants/${plantId}/statistics/years`);
  return response.data;
}

export async function getStatisticsGroups(plantId: string): Promise<StatisticsGroup[]> {
  const response = await apiClient.get<StatisticsGroup[]>(`/plants/${plantId}/statistics/groups`);
  return response.data;
}

export async function getStatisticsMetrics(plantId: string): Promise<StatisticsMetric[]> {
  const response = await apiClient.get<StatisticsMetric[]>(`/plants/${plantId}/statistics/metrics`);
  return response.data;
}

export async function postStatistics(
  plantId: string,
  items: MonthlyStatistics[],
): Promise<{ imported: number }> {
  const response = await apiClient.post(`/plants/${plantId}/statistics`, items);
  return response.data;
}

export async function getDailyStatistics(
  plantId: string,
  year: number,
  month: number,
  groupId?: number,
  metricTag?: string,
): Promise<DailyStatistics[]> {
  const response = await apiClient.get<DailyStatistics[]>(
    `/plants/${plantId}/statistics/daily`,
    { params: { year, month, groupId, metricTag } },
  );
  return response.data;
}

/**
 * Statistiche giornaliere su range di date libero (estremi inclusi, date locali
 * impianto, formato YYYY-MM-DD). Supporta range a cavallo di mese/anno.
 */
export async function getDailyStatisticsRange(
  plantId: string,
  from: string,
  to: string,
  groupId?: number,
  metricTag?: string,
): Promise<DailyStatistics[]> {
  const response = await apiClient.get<DailyStatistics[]>(
    `/plants/${plantId}/statistics/daily`,
    { params: { from, to, groupId, metricTag } },
  );
  return response.data;
}

// ─── Alert System ────────────────────────────────────────────────────────────

// Alert Rules
export async function getAlertRules(plantId: number): Promise<AlertRule[]> {
  const response = await apiClient.get<AlertRule[]>(`/alert/rules/plant/${plantId}`);
  return response.data;
}

export async function createAlertRule(rule: Partial<AlertRule>): Promise<AlertRule> {
  const response = await apiClient.post<AlertRule>('/alert/rules', rule);
  return response.data;
}

export async function updateAlertRule(id: number, rule: Partial<AlertRule>): Promise<AlertRule> {
  const response = await apiClient.put<AlertRule>(`/alert/rules/${id}`, rule);
  return response.data;
}

export async function deleteAlertRule(id: number): Promise<void> {
  await apiClient.delete(`/alert/rules/${id}`);
}

// Alert Events
export async function getAlertEvents(plantId: number, status?: string, limit = 100, offset = 0): Promise<{ events: AlertEvent[]; total: number }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (status) params.append('status', status);
  const response = await apiClient.get(`/alert/events/plant/${plantId}?${params}`);
  return response.data;
}

export async function getActiveAlertEvents(plantId: number): Promise<AlertEvent[]> {
  const response = await apiClient.get<AlertEvent[]>(`/alert/events/active/plant/${plantId}`);
  return response.data;
}

export async function acknowledgeAlertEvent(id: number, method?: string, notes?: string): Promise<AlertEvent> {
  const response = await apiClient.post<AlertEvent>(`/alert/events/${id}/acknowledge`, { method, notes });
  return response.data;
}

// On-Call Calendar
export async function getOnCallSchedules(plantId: number): Promise<OnCallSchedule[]> {
  const response = await apiClient.get<OnCallSchedule[]>(`/alert/oncall/plant/${plantId}`);
  return response.data;
}

export async function createOnCallSchedule(schedule: Partial<OnCallSchedule>): Promise<OnCallSchedule> {
  const response = await apiClient.post<OnCallSchedule>('/alert/oncall', schedule);
  return response.data;
}

export async function updateOnCallSchedule(id: number, schedule: Partial<OnCallSchedule>): Promise<OnCallSchedule> {
  const response = await apiClient.put<OnCallSchedule>(`/alert/oncall/${id}`, schedule);
  return response.data;
}

export async function deleteOnCallSchedule(id: number): Promise<void> {
  await apiClient.delete(`/alert/oncall/${id}`);
}

// Notification Channels
export async function getNotificationChannels(plantId: number): Promise<NotificationChannel[]> {
  const response = await apiClient.get<NotificationChannel[]>(`/alert/channels/plant/${plantId}`);
  return response.data;
}

export async function createNotificationChannel(channel: Record<string, unknown>): Promise<NotificationChannel> {
  const response = await apiClient.post<NotificationChannel>('/alert/channels', channel);
  return response.data;
}

export async function updateNotificationChannel(id: number, channel: Record<string, unknown>): Promise<NotificationChannel> {
  const response = await apiClient.put<NotificationChannel>(`/alert/channels/${id}`, channel);
  return response.data;
}

export async function deleteNotificationChannel(id: number): Promise<void> {
  await apiClient.delete(`/alert/channels/${id}`);
}

// Notification Logs
export async function getNotificationLogs(eventId: number): Promise<NotificationLog[]> {
  const response = await apiClient.get<NotificationLog[]>(`/alert/notifications/event/${eventId}`);
  return response.data;
}

// ─── Admin: System Notification Settings ─────────────────────────────────────

export async function getNotificationSettings(): Promise<import('../types').SystemNotificationSettings> {
  const response = await apiClient.get('/admin/notification-settings');
  return response.data;
}

export async function updateNotificationSettings(settings: Record<string, unknown>): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.put('/admin/notification-settings', settings);
  return response.data;
}

export async function testNotificationEmail(recipient: string): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post('/admin/notification-settings/test-email', { recipient });
  return response.data;
}

export async function testNotificationSms(recipient: string): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post('/admin/notification-settings/test-sms', { recipient });
  return response.data;
}

export async function testNotificationVoice(recipient: string): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post('/admin/notification-settings/test-voice', { recipient });
  return response.data;
}

export async function testSipStatus(): Promise<{ success: boolean; message: string; data?: Record<string, unknown> }> {
  const response = await apiClient.post('/admin/notification-settings/test-sip');
  return response.data;
}

export async function pushSipConfig(): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post('/admin/notification-settings/push-sip');
  return response.data;
}

export interface SipSyncStatus {
  /** True se nel backend è valorizzato Voice:SipBridgeUrl. False = niente da sincronizzare. */
  sipBridgeConfigured: boolean;
  /** Ultimo timestamp in cui il worker SipBridgeAutoSyncService ha tentato un push (UTC). */
  lastSyncAttempt: string | null;
  /** Ultimo push andato a buon fine (UTC). Se è "molto vecchio" rispetto ad attempt → in retry. */
  lastSyncSuccessAt: string | null;
  /** Errore dell'ultimo tentativo (null se l'ultimo è andato bene). */
  lastSyncError: string | null;
  /** Quanti retry consecutivi falliti (0 = ok). */
  consecutiveFailures: number;
}

/** Stato del worker auto-sync SIP (per il badge "Sincronizzato/In retry/Errore"). */
export async function getSipSyncStatus(): Promise<SipSyncStatus> {
  const response = await apiClient.get<SipSyncStatus>('/admin/notification-settings/sip-sync-status');
  return response.data;
}

// ─── Escalation Policy ──────────────────────────────────────────────────
export async function getEscalationPolicy(plantId: number) {
  const response = await apiClient.get(`/alert/escalation/plant/${plantId}`);
  return response.data;
}

export async function createEscalationPolicy(data: {
  plantId: number;
  name: string;
  mode: number;
  escalationIntervalSeconds: number;
  maxRetries: number;
  batchWindowSeconds: number;
  enabled: boolean;
}) {
  const response = await apiClient.post('/alert/escalation', data);
  return response.data;
}

export async function updateEscalationPolicy(id: number, data: {
  plantId: number;
  name: string;
  mode: number;
  escalationIntervalSeconds: number;
  maxRetries: number;
  batchWindowSeconds: number;
  enabled: boolean;
}) {
  const response = await apiClient.put(`/alert/escalation/${id}`, data);
  return response.data;
}

export async function deleteEscalationPolicy(id: number) {
  await apiClient.delete(`/alert/escalation/${id}`);
}

// ─── Plant Config (dashboard stats, trend templates) ────────────────────────

export async function getPlantConfig(plantId: number, key: string): Promise<string | null> {
  const response = await apiClient.get(`/plants/${plantId}/config/${key}`);
  return response.data.configJson ?? null;
}

export async function savePlantConfig(plantId: number, key: string, configJson: string): Promise<void> {
  await apiClient.put(`/plants/${plantId}/config/${key}`, { configJson });
}

// ─── TIA Portal Import ───────────────────────────────────────────────────────

export async function parseTiaPortalXml(plantId: number, xmlContent: string): Promise<import('../types').TiaPortalParseResponse> {
  const response = await apiClient.post(`/plants/${plantId}/variables/parse-tia-xml`, { xmlContent });
  return response.data;
}

// ─── Screenshot AI Import ────────────────────────────────────────────────────

/**
 * Analizza uno screenshot per estrarre variabili PLC tramite AI Vision.
 * @param userHints istruzioni in linguaggio naturale per guidare il modello (regole di
 *  naming, formato indirizzi, prefissi). Il backend le inietta nel prompt come override.
 *  Vuoto = comportamento default. Tipicamente memorizzato in localStorage per-plant.
 */
export async function parseScreenshot(
  plantId: number,
  file: File,
  userHints?: string,
): Promise<import('../types').VisionImportResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (userHints && userHints.trim()) {
    formData.append('userHints', userHints.trim());
  }
  const response = await apiClient.post(`/plants/${plantId}/variables/parse-screenshot`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000, // 60s timeout per AI analysis
  });
  return response.data;
}

// ─── QR Code Login ───────────────────────────────────────────────────────────

export async function generateQrToken(): Promise<{ token: string; expiresAt: string; qrUrl: string }> {
  const response = await apiClient.post('/auth/qr-token');
  return response.data;
}

export async function qrLogin(token: string): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/qr-login', { token });
  return response.data;
}

export async function revokeQrToken(): Promise<{ message: string; revoked: number }> {
  const response = await apiClient.delete('/auth/qr-token');
  return response.data;
}

// ─── Push Notifications ──────────────────────────────────────────────────────

export async function getVapidPublicKey(): Promise<{ publicKey: string }> {
  const response = await apiClient.get('/auth/vapid-public-key');
  return response.data;
}

export async function savePushSubscription(data: {
  endpoint: string; p256dh: string; auth: string;
  deviceName?: string; userAgent?: string;
}): Promise<{ message: string }> {
  const response = await apiClient.post('/auth/push-subscription', data);
  return response.data;
}

export async function removePushSubscription(data: { endpoint: string }): Promise<{ message: string }> {
  const response = await apiClient.delete('/auth/push-subscription', { data });
  return response.data;
}

export async function getPushSubscriptions(): Promise<import('../types').PushSubscriptionInfo[]> {
  const response = await apiClient.get('/auth/push-subscriptions');
  return response.data;
}

export async function testPushNotification(userId?: string): Promise<{ sent: number; total: number; message: string; errors: string[] }> {
  const params = userId ? `?userId=${userId}` : '';
  const response = await apiClient.post(`/auth/test-push${params}`);
  return response.data;
}

// ─── AI Vision Settings ───

export async function getVisionSettings(): Promise<{ provider: string; model: string; hasApiKey: boolean }> {
  const response = await apiClient.get('/admin/vision-settings');
  return response.data;
}

export async function updateVisionSettings(data: { provider?: string; apiKey?: string; model?: string }): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.put('/admin/vision-settings', data);
  return response.data;
}

// ─── Voice Assistant: Knowledge Base ───

export async function getPlantKnowledge(plantId: number, category?: string): Promise<PlantKnowledge[]> {
  const params = category ? `?category=${encodeURIComponent(category)}` : '';
  const response = await apiClient.get(`/voice/knowledge/${plantId}${params}`);
  return response.data;
}

export async function getKnowledgeSummary(plantId: number): Promise<KnowledgeSummary> {
  const response = await apiClient.get(`/voice/knowledge/${plantId}/summary`);
  return response.data;
}

export async function addPlantKnowledge(plantId: number, data: { category?: string; question: string; answer: string }): Promise<{ id: number }> {
  const response = await apiClient.post(`/voice/knowledge/${plantId}`, data);
  return response.data;
}

export async function updatePlantKnowledge(id: number, data: { category?: string; question?: string; answer?: string }): Promise<void> {
  await apiClient.put(`/voice/knowledge/entry/${id}`, data);
}

export async function deletePlantKnowledge(id: number): Promise<void> {
  await apiClient.delete(`/voice/knowledge/entry/${id}`);
}

// ─── Voice Assistant: Session History ───

export async function getVoiceSessionHistory(plantId?: number, limit = 50): Promise<VoiceSession[]> {
  const params = new URLSearchParams();
  if (plantId) params.set('plantId', plantId.toString());
  params.set('limit', limit.toString());
  const response = await apiClient.get(`/voice/sessions/history?${params}`);
  return response.data;
}

export async function getActiveVoiceSessions(): Promise<VoiceSession[]> {
  const response = await apiClient.get('/voice/sessions/active');
  return response.data;
}

// ─── Voice Assistant: Turn-by-turn review (Step 3) ───

export async function getVoiceSessionTurns(sessionId: number): Promise<VoiceSessionTurnsResponse> {
  const response = await apiClient.get(`/voice/sessions/${sessionId}/turns`);
  return response.data;
}

export async function setVoiceTurnFeedback(
  turnId: number,
  body: SetVoiceTurnFeedbackRequest
): Promise<void> {
  await apiClient.post(`/voice/turns/${turnId}/feedback`, body);
}

export async function getVoiceFeedbackTags(): Promise<string[]> {
  const response = await apiClient.get('/voice/turns/feedback-tags');
  return response.data;
}

// ─── Database Maintenance ──────────────────────────────────────────────

export interface DbMaintenanceParams {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
  variableId?: number;
  year?: number;
  month?: number;
  groupId?: number;
  metricTag?: string;
  severity?: string;
  status?: string;
  search?: string;
  sortField?: string;
  sortDir?: string;
}

export interface DbMaintenanceResponse<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export async function getMaintenanceData(
  plantId: number | string, table: 'history' | 'statistics' | 'integrals' | 'alarms', params: DbMaintenanceParams = {}
): Promise<DbMaintenanceResponse<Record<string, unknown>>> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') query.set(k, String(v)); });
  const response = await apiClient.get(`/plants/${plantId}/maintenance/${table}?${query}`);
  return response.data;
}

export async function updateMaintenanceRecord(
  plantId: number | string, table: string, id: number | string, data: Record<string, unknown>
): Promise<void> {
  await apiClient.put(`/plants/${plantId}/maintenance/${table}/${id}`, data);
}

export async function deleteMaintenanceRecord(
  plantId: number | string, table: string, id: number | string
): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/maintenance/${table}/${id}`);
}

export async function bulkDeleteMaintenance(
  plantId: number | string, table: string, body: Record<string, unknown>
): Promise<{ message: string; deleted: number }> {
  const response = await apiClient.post(`/plants/${plantId}/maintenance/${table}/bulk-delete`, body);
  return response.data;
}

export async function getMaintenanceCounts(plantId: number | string): Promise<{
  historyCount: number; statisticsCount: number; integralsCount: number; alarmsCount: number;
}> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/counts`);
  return response.data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Plant Maintenance Module
// ═══════════════════════════════════════════════════════════════════════════

export async function getMaintDashboard(plantId: number): Promise<import('../types').MaintDashboard> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/dashboard`);
  return response.data;
}

export async function getMaintPlantTree(plantId: number): Promise<import('../types').MaintPlantTree> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/tree`);
  return response.data;
}

// Subsystems
export async function getMaintSubsystems(plantId: number): Promise<import('../types').MaintSubsystem[]> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/subsystems`);
  return response.data;
}

export async function createMaintSubsystem(plantId: number, data: { code: string; name: string; description?: string; groupId?: number }): Promise<import('../types').MaintSubsystem> {
  const response = await apiClient.post(`/plants/${plantId}/maintenance/subsystems`, data);
  return response.data;
}

export async function updateMaintSubsystem(plantId: number, subsystemId: number, data: { code?: string; name?: string; description?: string; groupId?: number; isActive?: boolean }): Promise<void> {
  await apiClient.put(`/plants/${plantId}/maintenance/subsystems/${subsystemId}`, data);
}

export async function deleteMaintSubsystem(plantId: number, subsystemId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/maintenance/subsystems/${subsystemId}`);
}

// Categories
export async function getMaintCategories(plantId: number): Promise<import('../types').MaintCategory[]> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/categories`);
  return response.data;
}

export async function createMaintCategory(plantId: number, data: { name: string; description?: string; parentCategoryId?: number }): Promise<import('../types').MaintCategory> {
  const response = await apiClient.post(`/plants/${plantId}/maintenance/categories`, data);
  return response.data;
}

export async function updateMaintCategory(plantId: number, categoryId: number, data: { name?: string; description?: string; parentCategoryId?: number; isActive?: boolean }): Promise<void> {
  await apiClient.put(`/plants/${plantId}/maintenance/categories/${categoryId}`, data);
}

export async function deleteMaintCategory(plantId: number, categoryId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/maintenance/categories/${categoryId}`);
}

// Components
export async function getMaintComponents(plantId: number, params?: {
  page?: number; pageSize?: number; search?: string; criticality?: string;
  groupId?: number; subsystemId?: number; categoryId?: number; isActive?: boolean;
  parentComponentId?: number; onlyRoots?: boolean;
  sortField?: string; sortDir?: string;
}): Promise<import('../types').MaintPagedResult<import('../types').MaintComponent>> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/components`, { params });
  return response.data;
}

export async function getMaintComponent(plantId: number, componentId: number): Promise<import('../types').MaintComponent> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/components/${componentId}`);
  return response.data;
}

export async function createMaintComponent(plantId: number, data: Record<string, unknown>): Promise<import('../types').MaintComponent> {
  const response = await apiClient.post(`/plants/${plantId}/maintenance/components`, data);
  return response.data;
}

export async function updateMaintComponent(plantId: number, componentId: number, data: Record<string, unknown>): Promise<void> {
  await apiClient.put(`/plants/${plantId}/maintenance/components/${componentId}`, data);
}

export async function deleteMaintComponent(plantId: number, componentId: number, force = false): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/maintenance/components/${componentId}`, {
    params: force ? { force: true } : undefined,
  });
}

// Counters
export async function getMaintCounters(plantId: number, componentId: number): Promise<import('../types').MaintCounter[]> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/components/${componentId}/counters`);
  return response.data;
}

export async function createMaintCounter(plantId: number, componentId: number, data: Record<string, unknown>): Promise<import('../types').MaintCounter> {
  const response = await apiClient.post(`/plants/${plantId}/maintenance/components/${componentId}/counters`, data);
  return response.data;
}

export async function updateMaintCounter(plantId: number, componentId: number, counterId: number, data: { currentValue?: number; name?: string; description?: string; unit?: string }): Promise<void> {
  await apiClient.put(`/plants/${plantId}/maintenance/components/${componentId}/counters/${counterId}`, data);
}

export async function deleteMaintCounter(plantId: number, componentId: number, counterId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/maintenance/components/${componentId}/counters/${counterId}`);
}

// Rules
export async function getMaintRules(plantId: number, params?: {
  componentId?: number; isActive?: boolean; ruleType?: string;
}): Promise<import('../types').MaintRule[]> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/rules`, { params });
  return response.data;
}

export async function createMaintRule(plantId: number, componentId: number, data: Record<string, unknown>): Promise<import('../types').MaintRule> {
  const response = await apiClient.post(`/plants/${plantId}/maintenance/components/${componentId}/rules`, data);
  return response.data;
}

export async function updateMaintRule(plantId: number, ruleId: number, data: Record<string, unknown>): Promise<void> {
  await apiClient.put(`/plants/${plantId}/maintenance/rules/${ruleId}`, data);
}

export async function deleteMaintRule(plantId: number, ruleId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/maintenance/rules/${ruleId}`);
}

// Interventions
export async function getMaintInterventions(plantId: number, params?: {
  page?: number; pageSize?: number; status?: string; priority?: string;
  componentId?: number; search?: string; sortField?: string; sortDir?: string;
}): Promise<import('../types').MaintPagedResult<import('../types').MaintIntervention>> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/interventions`, { params });
  return response.data;
}

export async function createMaintIntervention(plantId: number, data: Record<string, unknown>): Promise<import('../types').MaintIntervention> {
  const response = await apiClient.post(`/plants/${plantId}/maintenance/interventions`, data);
  return response.data;
}

export async function updateMaintIntervention(plantId: number, interventionId: number, data: Record<string, unknown>): Promise<void> {
  await apiClient.put(`/plants/${plantId}/maintenance/interventions/${interventionId}`, data);
}

// Work Reports
export async function getMaintWorkReport(plantId: number, interventionId: number): Promise<import('../types').MaintWorkReport> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/interventions/${interventionId}/report`);
  return response.data;
}

export async function createMaintWorkReport(plantId: number, data: Record<string, unknown>): Promise<import('../types').MaintWorkReport> {
  const response = await apiClient.post(`/plants/${plantId}/maintenance/reports`, data);
  return response.data;
}

export async function updateMaintWorkReport(plantId: number, reportId: number, data: Record<string, unknown>): Promise<void> {
  await apiClient.put(`/plants/${plantId}/maintenance/reports/${reportId}`, data);
}

// Materials
export async function addMaintMaterial(plantId: number, reportId: number, data: Record<string, unknown>): Promise<import('../types').MaintMaterial> {
  const response = await apiClient.post(`/plants/${plantId}/maintenance/reports/${reportId}/materials`, data);
  return response.data;
}

export async function deleteMaintMaterial(plantId: number, materialId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/maintenance/materials/${materialId}`);
}

// Tag Lookup (QR/RFID)
export async function lookupMaintTag(tag: string): Promise<import('../types').MaintTagLookupResult> {
  const response = await apiClient.get('/maintenance/lookup', { params: { tag } });
  return response.data;
}

// Attachments (polimorfico: componente / intervento / rapportino)
export async function getMaintAttachments(plantId: number, params: {
  componentId?: number; interventionId?: number; workReportId?: number;
}): Promise<import('../types').MaintAttachment[]> {
  const response = await apiClient.get(`/plants/${plantId}/maintenance/attachments`, { params });
  return response.data;
}

export async function uploadMaintAttachment(plantId: number, file: File, params: {
  componentId?: number; interventionId?: number; workReportId?: number;
  category?: string; description?: string;
}): Promise<import('../types').MaintAttachment> {
  const form = new FormData();
  form.append('file', file);
  if (params.componentId != null) form.append('componentId', String(params.componentId));
  if (params.interventionId != null) form.append('interventionId', String(params.interventionId));
  if (params.workReportId != null) form.append('workReportId', String(params.workReportId));
  if (params.category) form.append('category', params.category);
  if (params.description) form.append('description', params.description);
  const response = await apiClient.post(`/plants/${plantId}/maintenance/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function deleteMaintAttachment(plantId: number, attachmentId: number): Promise<void> {
  await apiClient.delete(`/plants/${plantId}/maintenance/attachments/${attachmentId}`);
}

/**
 * Ruota un allegato PDF di manutenzione in modo persistente (sovrascrive il blob).
 * `degrees` deve essere un multiplo di 90. La rotazione si applica a tutte le pagine.
 */
export async function rotateMaintAttachment(
  plantId: number,
  attachmentId: number,
  degrees: number
): Promise<void> {
  await apiClient.post(
    `/plants/${plantId}/maintenance/attachments/${attachmentId}/rotate`,
    null,
    { params: { degrees } }
  );
}

export function getMaintAttachmentDownloadUrl(plantId: number, attachmentId: number): string {
  return `${apiClient.defaults.baseURL}/plants/${plantId}/maintenance/attachments/${attachmentId}/download`;
}

export function getMaintAttachmentPreviewUrl(plantId: number, attachmentId: number): string {
  return `${apiClient.defaults.baseURL}/plants/${plantId}/maintenance/attachments/${attachmentId}/preview`;
}

/** Fetches preview as a blob URL with auth header (inline <img> cannot send Bearer). */
export async function fetchMaintAttachmentPreviewBlob(plantId: number, attachmentId: number): Promise<string> {
  const response = await apiClient.get(
    `/plants/${plantId}/maintenance/attachments/${attachmentId}/preview`,
    { responseType: 'blob' }
  );
  return URL.createObjectURL(response.data);
}

/** SAS URL per anteprima inline dell'allegato (preferito quando blob storage e' attivo). */
export async function getMaintAttachmentPreviewSasUrl(
  plantId: number, attachmentId: number
): Promise<BlobUrlResponse> {
  const response = await apiClient.get<BlobUrlResponse>(
    `/plants/${plantId}/maintenance/attachments/${attachmentId}/preview-url`
  );
  return response.data;
}

/** SAS URL per download dell'allegato. */
export async function getMaintAttachmentDownloadSasUrl(
  plantId: number, attachmentId: number
): Promise<BlobUrlResponse> {
  const response = await apiClient.get<BlobUrlResponse>(
    `/plants/${plantId}/maintenance/attachments/${attachmentId}/download-url`
  );
  return response.data;
}

/**
 * Risolve l'URL migliore per mostrare l'anteprima di un allegato (SAS se disponibile,
 * altrimenti object URL via fetch streaming).
 */
export async function resolveMaintAttachmentPreviewUrl(
  plantId: number, attachmentId: number
): Promise<string> {
  try {
    const sas = await getMaintAttachmentPreviewSasUrl(plantId, attachmentId);
    if (sas.usesBlobStorage && sas.url) return sas.url;
  } catch {
    // fallback
  }
  return fetchMaintAttachmentPreviewBlob(plantId, attachmentId);
}

// ── Link documenti impianto <-> componenti ────────────────────────────────

export async function getComponentDocumentLinks(
  plantId: number, componentId: number
): Promise<import('../types').MaintComponentDocument[]> {
  const response = await apiClient.get(
    `/plants/${plantId}/maintenance/components/${componentId}/documents`
  );
  return response.data;
}

export async function addComponentDocumentLink(
  plantId: number, componentId: number,
  plantDocumentId: number, documentRole?: string
): Promise<import('../types').MaintComponentDocument> {
  const response = await apiClient.post(
    `/plants/${plantId}/maintenance/components/${componentId}/documents`,
    { plantDocumentId, documentRole }
  );
  return response.data;
}

export async function deleteComponentDocumentLink(
  plantId: number, componentId: number, linkId: number
): Promise<void> {
  await apiClient.delete(
    `/plants/${plantId}/maintenance/components/${componentId}/documents/${linkId}`
  );
}

// ── Camere IP (publish-on-demand via MediaMTX) ────────────────────────────

/**
 * Estrae un messaggio d'errore leggibile dalla risposta axios / errore generico.
 * Priorità: `response.data.message` → `response.data.title` (ProblemDetails) →
 * `message` → fallback.
 */
export function extractApiErrorMessage(err: any, fallback = 'Errore'): string {
  return (
    err?.response?.data?.message
    ?? err?.response?.data?.title
    ?? err?.response?.data?.error
    ?? (typeof err?.response?.data === 'string' ? err.response.data : undefined)
    ?? err?.message
    ?? fallback
  );
}

export async function getCamerasByPlant(
  plantId: number
): Promise<import('../types').Camera[]> {
  const response = await apiClient.get(`/plants/${plantId}/cameras`);
  return response.data;
}

/** Alias di {@link getCamerasByPlant}, stesso endpoint. */
export const getPlantCameras = getCamerasByPlant;

/**
 * Mosaico cross-plant: ritorna tutte le camere che l'utente corrente può vedere
 * (unione delle camere degli impianti in cui ha `CamerasView`). Endpoint backend
 * `GET /cameras/accessible`.
 */
export async function getAccessibleCameras(): Promise<import('../types').Camera[]> {
  const response = await apiClient.get('/cameras/accessible');
  return response.data;
}

export async function requestCameraViewerToken(
  cameraId: number
): Promise<import('../types').ViewerTokenResponse> {
  const response = await apiClient.post(`/cameras/${cameraId}/viewer-token`);
  return response.data;
}

export async function releaseCameraViewer(
  cameraId: number, sessionId: string
): Promise<void> {
  await apiClient.post(`/cameras/${cameraId}/viewer-release`, null, {
    params: { sessionId },
  });
}

export async function getCameraById(
  cameraId: number
): Promise<import('../types').Camera> {
  const response = await apiClient.get(`/cameras/${cameraId}`);
  return response.data;
}

export async function createCamera(
  plantId: number, dto: import('../types').CreateCameraDto
): Promise<import('../types').Camera> {
  const response = await apiClient.post(`/plants/${plantId}/cameras`, dto);
  return response.data;
}

export async function updateCamera(
  cameraId: number, dto: import('../types').UpdateCameraDto
): Promise<import('../types').Camera> {
  const response = await apiClient.put(`/cameras/${cameraId}`, dto);
  return response.data;
}

export async function deleteCamera(cameraId: number): Promise<void> {
  await apiClient.delete(`/cameras/${cameraId}`);
}

export async function probeCamera(
  cameraId: number
): Promise<import('../types').CameraProbeResultDto> {
  const response = await apiClient.post(`/cameras/${cameraId}/probe`);
  return response.data;
}

// ─── Mosaic templates (TVCC) ───────────────────────────────────────────
//
// Visibilità: server-side via company-intersection. Il client riceve gia'
// filtrati i template che l'utente puo' vedere (template visibile sse l'insieme
// delle aziende coinvolte e' subset delle aziende dell'utente).
// Le preferenze "default per scope" sono per-utente (no piu' isDefault per template).

export async function listMosaicTemplates(): Promise<import('../types').MosaicTemplate[]> {
  const response = await apiClient.get('/mosaic-templates');
  return response.data;
}

export async function createMosaicTemplate(
  dto: import('../types').CreateMosaicTemplateDto
): Promise<import('../types').MosaicTemplate> {
  const response = await apiClient.post('/mosaic-templates', dto);
  return response.data;
}

export async function updateMosaicTemplate(
  id: number, dto: import('../types').UpdateMosaicTemplateDto
): Promise<import('../types').MosaicTemplate> {
  const response = await apiClient.put(`/mosaic-templates/${id}`, dto);
  return response.data;
}

export async function deleteMosaicTemplate(id: number): Promise<void> {
  await apiClient.delete(`/mosaic-templates/${id}`);
}

export async function getMosaicPreferences(): Promise<import('../types').MosaicPreference[]> {
  const response = await apiClient.get('/mosaic-templates/preferences');
  return response.data;
}

/** Imposta o rimuove (defaultTemplateId=null) il default template per uno scope.
 * Scope: "cross" oppure "plant_{plantId}". */
export async function setMosaicPreference(
  scope: string, defaultTemplateId: number | null
): Promise<import('../types').MosaicPreference> {
  const response = await apiClient.put('/mosaic-templates/preferences', {
    scope,
    defaultTemplateId,
  });
  return response.data;
}

export default apiClient;
